import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';
import { format, parseISO, isValid } from 'date-fns';

/**
 * 🛰️ TIMEZONE BRIDGE v1.8
 * Standardizes the "Translation Layer" between Firestore (UTC) and UI (Wall Clock).
 */

/**
 * Returns a list of all IANA timezone strings supported by the browser.
 */
export const getAllTimezones = (): string[] => {
  return Intl.supportedValuesOf('timeZone');
};

/**
 * 📥 FROM DATABASE -> TO UI
 * Converts a UTC Date (or ISO string) into the User's "Wall Clock" Date object.
 * This is used to position events on the Grid.
 */
export const toDisplayDate = (date: Date | number | string, timeZone: string): Date => {
  if (!date) throw new Error(`toDisplayDate: Received null or undefined date.`);

  // Parse if string, otherwise wrap
  const baseDate = typeof date === 'string' ? parseISO(date) : new Date(date);
  
  if (!isValid(baseDate)) {
    throw new Error(`toDisplayDate: Invalid date format received: ${date}`);
  }

  // toZonedTime shifts the UTC moment to the target perspective
  // Example: 12:00 UTC -> 07:00 EST
  return toZonedTime(baseDate, timeZone);
};

/**
 * 📤 FROM UI -> TO DATABASE
 * Converts the User's "Wall Clock" (e.g., from a <input type="datetime-local">) 
 * into a pure UTC Date for Firestore storage.
 */
export const toStorageDate = (date: Date | string, timeZone: string): Date => {
  if (!date) throw new Error(`toStorageDate: Received null or undefined date.`);

  let wallClockDate: Date;

  if (typeof date === 'string') {
    /**
     * datetime-local inputs return "YYYY-MM-DDTHH:mm".
     * parseISO treats this as a local floating time.
     */
    wallClockDate = parseISO(date);
  } else {
    wallClockDate = date;
  }

  if (!isValid(wallClockDate)) {
    throw new Error(`toStorageDate: Invalid wall-clock date received: ${date}`);
  }

  /**
   * fromZonedTime is the "Perspective Lock":
   * It takes the raw 10:00 AM and asks: 
   * "If it is 10:00 AM in [timeZone], what is the absolute UTC moment?"
   */
  return fromZonedTime(wallClockDate, timeZone);
};

/**
 * 📝 FORMAT ZONED
 * Formats a Date object specifically for a given timezone without changing the underlying value.
 */
export const formatZoned = (date: Date | string, timeZone: string, pattern: string = 'PPpp'): string => {
  const d = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(d)) throw new Error(`formatZoned: Cannot format invalid date.`);
  
  return formatInTimeZone(d, timeZone, pattern);
};

/**
 * 📏 GMT OFFSET CALCULATOR
 * Returns "GMT-05:00" style strings for UI status bars.
 */
export const getTimezoneOffset = (timeZone: string): string => {
  const reference = new Date(); 
  try {
    return "GMT" + formatInTimeZone(reference, timeZone, 'xxx'); 
  } catch (error) {
    return "UTC"; // Graceful fallback
  }
};

/**
 * 🌓 ISO TRANSITION (v1.8 Helper)
 * Specialized for datetime-local inputs to ensure we don't lose seconds/milliseconds
 */
export const toInputString = (date: Date, timeZone: string): string => {
  if (!isValid(date)) return '';
  return formatInTimeZone(date, timeZone, "yyyy-MM-dd'T'HH:mm");
};